import React from 'react';
import classNames from 'classnames';

import { propTypes } from '../../../util/types';
import { ListingCard, PaginationLinks } from '../../../components';

import css from './SearchResultsPanel.module.css';

/**
 * SearchResultsPanel component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.listing>} props.listings - The listings
 * @param {propTypes.pagination} props.pagination - The pagination
 * @param {Object} props.search - The search
 * @param {Function} props.setActiveListing - The function to handle the active listing
 * @param {boolean} [props.isMapVariant] - Whether the map variant is enabled
 * @returns {JSX.Element}
 */
const SearchResultsPanel = props => {
  const {
    className,
    rootClassName,
    listings = [],
    pagination,
    search,
    setActiveListing,
    isMapVariant = true,
    listingTypeParam,
    reviewSummaries,

    // Optional props for Inquiry modal behavior in ListingCard
    onSubmitInquiry,
    onManageDisableScrolling,
    sendInquiryError,
    sendInquiryInProgress,

    noResultsInfo,

  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const pageName = listingTypeParam ? 'SearchPageWithListingType' : 'SearchPage';

  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName={pageName}
        pagePathParams={{ listingType: listingTypeParam }}
        pageSearchParams={search}
        pagination={pagination}
      />
    ) : null;

  const cardRenderSizes = isMapVariant => {
    if (isMapVariant) {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 767px) 100vw',
        `(max-width: 1023px) ${panelMediumWidth}vw`,
        `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
        `${panelLargeWidth / 3}vw`,
      ].join(', ');
    } else {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 549px) 100vw',
        '(max-width: 767px) 50vw',
        `(max-width: 1439px) 26vw`,
        `(max-width: 1920px) 18vw`,
        `14vw`,
      ].join(', ');
    }
  };

  return (
    <div className={classes}>
      <div className={isMapVariant ? css.listingCardsMapVariant : css.listingCards}>
        {listings.map(l => {
          const uuid = l?.id?.uuid;
          const summaryForListing = reviewSummaries && uuid ? reviewSummaries[uuid] : undefined;
          // Bind per-listing inquiry submit handler: ListingCard expects (values) only
          const cardOnSubmitInquiry = onSubmitInquiry
            ? values => onSubmitInquiry(l, values)
            : null;
          return (
            <ListingCard
              className={css.listingCard}
              key={uuid}
              listing={l}
              renderSizes={cardRenderSizes(isMapVariant)}
              setActiveListing={setActiveListing}
              reviewSummaries={summaryForListing}
              onSubmitInquiry={cardOnSubmitInquiry}
              onManageDisableScrolling={onManageDisableScrolling}
              sendInquiryError={sendInquiryError}
              sendInquiryInProgress={sendInquiryInProgress}
            />
          );
        })}
        {props.children}
        {noResultsInfo && <div className={css.noResultsInfo}>{noResultsInfo}</div>}
      </div>
      {paginationLinks}
    </div>
  );
};

export default SearchResultsPanel;
